import { Component, Input, OnChanges, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ColumnModel } from '@syncfusion/ej2-angular-grids';
import { KwFilterDropdownService } from '../../../../../core/widgets/kw-filter-dropdown/kw-filter-dropdown.service';
import { HandlingUnitBulkMove } from '../../../models/handling-unit-bulk-actions';
import { handlingUnitLocation, HandlingUnitMasterDTO } from '../../../models/handling-unit-master';
import { inventoryStatusClass } from '../../../models/inventory-status';
import { InventorySessionService } from '../../../services/inventory-session.service';
import { BulkMoveSelectionPopupComponent } from '../../inventory-summary/bulk-move-selection-popup/bulk-move-selection-popup.component';
import { groupUnitRows, LotUnitRow } from '../lot-unit-summary';

@Component({
  selector: 'lot-barcode-summary',
  templateUrl: './lot-barcode-summary.component.html',
  styleUrls: ['./lot-barcode-summary.component.scss']
})
export class LotBarcodeSummaryComponent implements OnChanges {
  @Input() allHandlingUnits: HandlingUnitMasterDTO[] = [];
  @Input() lotId: number;
  @Input() lotNumber: string;

  @ViewChild('statusTemplate', { static: true }) public statusTemplate: any;

  public rows: LotUnitRow[] = [];
  public leadColumns: ColumnModel[] = [];
  public readonly statusClass = inventoryStatusClass;

  constructor(
    private dialog: MatDialog,
    public filterService: KwFilterDropdownService,
    private session: InventorySessionService) { }

  ngOnChanges() {
    const hums = this.allHandlingUnits ?? [];
    const firstByBarcode = new Map<string, HandlingUnitMasterDTO>();
    hums.forEach(hum => { if (!firstByBarcode.has(hum.barcode)) firstByBarcode.set(hum.barcode, hum); });

    this.rows = groupUnitRows(hums, hum => hum.barcode, hum => (hum.handlingUnitDetails ?? []).filter(hud => hud.lotId == this.lotId))
      .map(row => {
        const hum = firstByBarcode.get(row.key);
        return { ...row, status: hum?.inventoryStatusName ?? '', location: handlingUnitLocation(hum) || 'Unknown' };
      });

    this.leadColumns = [
      { field: 'status', headerText: 'Status', template: this.statusTemplate, autoFit: true },
      { field: 'location', headerText: 'Location', clipMode: 'EllipsisWithTooltip', autoFit: true }
    ];
  }

  /** Stages every barcode in this lot for the bulk move dialog. */
  bulkAction() {
    const lotHuds = (this.allHandlingUnits ?? []).flatMap(hum => hum.handlingUnitDetails ?? []).filter(hud => hud.lotId == this.lotId);
    const masterIds = lotHuds.map(hud => hud.handlingUnitMasterId);
    const hums = this.allHandlingUnits.filter(hum => masterIds.includes(hum.id));

    const byBarcode = new Map<string, HandlingUnitMasterDTO[]>();
    hums.forEach(hum => byBarcode.set(hum.barcode, [...(byBarcode.get(hum.barcode) ?? []), hum]));

    const bulkMoveList: HandlingUnitBulkMove[] = [];
    for (const group of byBarcode.values()) {
      const hum = group[0];
      const warehouse = this.filterService.allWarehouses.find(y => y.id == hum.warehouseId);
      const room = this.filterService.allRooms.find(y => y.id == hum.roomId);
      const location = this.filterService.allLocations.find(y => y.id == hum.roomLocationId);

      bulkMoveList.push({
        humId: hum.id,
        warehouseId: hum.warehouseId || 0,
        roomId: hum.roomId || 0,
        roomLocationId: hum.roomLocationId || 0,
        roomLocationName: location?.name ?? '',
        roomName: room?.name ?? '',
        warehouseName: warehouse?.name ?? '',
        tagNumber: hum.barcode,
        details: [...new Set(group.flatMap(h => h.handlingUnitDetails ?? []).filter(hud => hud.lotId == this.lotId).map(hud => hud.itemName))].join(', '),
        lot: this.lotNumber,
        isMultiple: true,
        boxStart: hum.boxStart || '',
        boxEnd: hum.boxEnd || '',
        integrationId: hum.integrationId || '',
        dateIn: hum.dateIn || '',
        dateOut: hum.dateOut || ''
      });

      // The bulk move dialog reads every selected HUM id off the first entry.
      bulkMoveList[0].handlingUnitMasterIds = [...(bulkMoveList[0].handlingUnitMasterIds ?? []), hum.id];
    }

    this.session.handlingUnitBulkMoveList = bulkMoveList;
    this.dialog.open(BulkMoveSelectionPopupComponent, { minWidth: '550px', data: {} });
  }
}
